<template>
  <div class="legacy-report-editor">
    <form-section
      title="Реквизиты отчета"
      description="Реквизиты сохраняются в исходном формате документа. Печатная форма привязана к выбранной версии."
      :disabled="disabled"
    >
      <form-grid>
        <q-input
          :model-value="body.in_org_number"
          outlined
          dense
          label="Номер отчета"
          data-testid="legacy-report-number"
          @update:model-value="updateField('in_org_number', $event)"
        />
        <q-input
          :model-value="dateInputs.document_date"
          outlined
          dense
          label="Дата документа"
          mask="##.##.####"
          hint="ДД.ММ.ГГГГ"
          data-testid="legacy-report-document-date"
          @update:model-value="updateDate('document_date', $event)"
        />
        <q-input
          :model-value="dateInputs.date_current"
          outlined
          dense
          label="Отчетная дата"
          mask="##.##.####"
          hint="ДД.ММ.ГГГГ"
          data-testid="legacy-report-current-date"
          @update:model-value="updateDate('date_current', $event)"
        />
        <q-input
          :model-value="dateInputs.date_previous"
          outlined
          dense
          label="Предыдущий период"
          mask="##.##.####"
          hint="ДД.ММ.ГГГГ"
          @update:model-value="updateDate('date_previous', $event)"
        />
        <q-input
          v-if="dateInputs.date_preprevious"
          :model-value="dateInputs.date_preprevious"
          outlined
          dense
          label="Позапрошлый период"
          mask="##.##.####"
          hint="ДД.ММ.ГГГГ"
          @update:model-value="updateDate('date_preprevious', $event)"
        />
        <q-input
          :model-value="measureValue"
          outlined
          dense
          label="Единица измерения"
          @update:model-value="updateMeasure"
        />
      </form-grid>
    </form-section>

    <form-section
      title="Организация"
      description="Снимок реквизитов относится к выбранной версии отчета."
      :disabled="disabled"
    >
      <form-grid>
        <q-input
          :model-value="body.organisation_name"
          outlined
          dense
          label="Наименование"
          @update:model-value="updateField('organisation_name', $event)"
        />
        <q-input
          :model-value="body.organisation_inn"
          outlined
          dense
          label="ИНН"
          data-testid="legacy-report-organisation-inn"
          @update:model-value="updateField('organisation_inn', $event)"
        />
        <q-input
          :model-value="body.organisation_form"
          outlined
          dense
          label="Организационно-правовая форма"
          @update:model-value="updateField('organisation_form', $event)"
        />
        <q-input
          :model-value="body.organisation_econ"
          outlined
          dense
          label="Вид деятельности"
          @update:model-value="updateField('organisation_econ', $event)"
        />
        <q-input
          :model-value="body.organisation_address"
          outlined
          dense
          label="Адрес"
          type="textarea"
          autogrow
          class="legacy-report-editor__full"
          @update:model-value="updateField('organisation_address', $event)"
        />
      </form-grid>
    </form-section>

    <form-section
      v-if="hasAuditFields"
      title="Аудитор"
      description="Поля выводятся только потому, что они присутствуют в сохраненной форме."
      :disabled="disabled"
    >
      <form-grid>
        <q-toggle
          :model-value="Boolean(body.audit)"
          label="Отчетность подлежит обязательному аудиту"
          class="legacy-report-editor__full"
          @update:model-value="updateField('audit', $event)"
        />
        <q-input
          :model-value="body.organisation_audit_name"
          outlined
          dense
          label="Наименование аудитора"
          @update:model-value="updateField('organisation_audit_name', $event)"
        />
        <q-input
          :model-value="body.organisation_audit_id"
          outlined
          dense
          label="ИНН / ОГРН аудитора"
          @update:model-value="updateField('organisation_audit_id', $event)"
        />
        <q-input
          :model-value="body.organisation_audit_num"
          outlined
          dense
          label="Номер в реестре аудиторов"
          @update:model-value="updateField('organisation_audit_num', $event)"
        />
      </form-grid>
    </form-section>

    <form-section
      title="Показатели"
      description="Широкая форма прокручивается только внутри таблицы. Неизвестные поля исходного body сохраняются без изменений."
      :disabled="disabled"
    >
      <div
        class="legacy-report-editor__table-region"
        role="region"
        aria-label="Редактируемые показатели отчета"
        tabindex="0"
        data-sid-scroll-region
        data-testid="legacy-report-editor-table"
      >
        <table>
          <thead>
            <tr>
              <th scope="col">Код</th>
              <th scope="col">Показатель</th>
              <th v-for="column in columns" :key="column.name" scope="col">{{ column.label }}</th>
              <th v-if="showDescription" scope="col">Пояснение</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.key" :class="{ 'legacy-report-editor__total': row.isTotal }">
              <td>{{ row.code || '—' }}</td>
              <th scope="row">
                <span v-if="row.section" class="legacy-report-editor__section">{{ row.section }}</span>
                {{ row.name }}
              </th>
              <td v-for="column in columns" :key="column.name">
                <q-input
                  v-if="hasRowField(row.key, column.name)"
                  :model-value="displayRowValue(row.key, column.name)"
                  outlined
                  dense
                  :aria-label="`${row.name}: ${column.label}`"
                  :data-testid="`legacy-report-value-${row.key}-${column.name}`"
                  @blur="editingValue === `${row.key}:${column.name}` && (editingValue = '')"
                  @update:model-value="editingValue = `${row.key}:${column.name}`; updateRow(row.key, column.name, $event)"
                />
                <span v-else aria-hidden="true">—</span>
              </td>
              <td v-if="showDescription">
                <q-input
                  v-if="hasDescription(row.key)"
                  :model-value="body[row.key].desc"
                  outlined
                  dense
                  :aria-label="`${row.name}: пояснение`"
                  @update:model-value="updateRow(row.key, 'desc', $event)"
                />
                <span v-else aria-hidden="true">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </form-section>
  </div>
</template>

<script>
import { FormGrid, FormSection } from '../../../../components/ui'
import { formatMoney } from '../utils/reportFormatters'

export default {
  name: 'LegacyReportEditor',
  components: { FormGrid, FormSection },
  emits: ['update-body-field', 'update-date', 'update-row-value'],
  props: {
    body: { type: Object, required: true },
    columns: { type: Array, required: true },
    dateInputs: { type: Object, required: true },
    disabled: { type: Boolean, default: false },
    rows: { type: Array, required: true }
  },
  data() { return { editingValue: '' } },
  computed: {
    measureValue() {
      const value = this.body.currency_measure
      return value && typeof value === 'object' ? (value.label || value.value || '') : (value || '')
    },
    hasAuditFields() {
      return ['audit', 'organisation_audit_name', 'organisation_audit_id', 'organisation_audit_num']
        .some((key) => Object.prototype.hasOwnProperty.call(this.body, key))
    },
    showDescription() {
      return this.rows.some((row) => this.hasDescription(row.key))
    }
  },
  methods: {
    hasDescription(rowKey) {
      return this.body[rowKey] && Object.prototype.hasOwnProperty.call(this.body[rowKey], 'desc')
    },
    hasRowField(rowKey, field) {
      return this.body[rowKey] && Object.prototype.hasOwnProperty.call(this.body[rowKey], field)
    },
    displayRowValue(rowKey, columnName) {
      const value = this.rowValue(rowKey, columnName)
      return !this.disabled && this.editingValue === `${rowKey}:${columnName}` ? value : formatMoney(value, { useGrouping: false, empty: '' })
    },
    rowValue(rowKey, columnName) {
      const row = this.body[rowKey] || {}
      return Object.prototype.hasOwnProperty.call(row, columnName) ? row[columnName] : ''
    },
    updateDate(key, value) {
      this.$emit('update-date', { key, value })
    },
    updateField(key, value) {
      this.$emit('update-body-field', { key, value })
    },
    updateMeasure(value) {
      const current = this.body.currency_measure
      this.updateField('currency_measure', current && typeof current === 'object'
        ? { ...current, label: value }
        : value)
    },
    updateRow(rowKey, field, value) {
      this.$emit('update-row-value', { rowKey, field, value })
    }
  }
}
</script>

<style scoped>
.legacy-report-editor {
  display: grid;
  gap: var(--sid-space-6);
}

.legacy-report-editor__full {
  --form-grid-span: 12;
}

.legacy-report-editor__table-region {
  max-width: 100%;
  overflow-x: auto;
  border: 1px solid var(--sid-color-line);
  border-radius: var(--sid-radius-surface);
}

.legacy-report-editor table {
  width: 100%;
  min-width: 58rem;
  border-collapse: collapse;
}

.legacy-report-editor th,
.legacy-report-editor td {
  padding: var(--sid-space-2);
  border-bottom: 1px solid var(--sid-color-line);
  text-align: left;
  vertical-align: top;
}

.legacy-report-editor thead th {
  position: sticky;
  z-index: 1;
  top: 0;
  background: var(--sid-color-surface-subtle);
  color: var(--sid-color-text-secondary);
  font-size: var(--sid-font-size-label);
}

.legacy-report-editor tbody th {
  min-width: 18rem;
  font-weight: var(--sid-font-weight-regular);
}

.legacy-report-editor td {
  min-width: 10rem;
}

.legacy-report-editor__section {
  display: block;
  margin-bottom: var(--sid-space-1);
  color: var(--sid-color-text-secondary);
  font-size: var(--sid-font-size-label);
}

.legacy-report-editor__total th,
.legacy-report-editor__total td {
  background: var(--sid-color-surface-subtle);
  font-weight: var(--sid-font-weight-semibold);
}
</style>
